import type { RealEstatePlanCode } from '@crm/shared/frontend';
import { REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES } from '@crm/shared/real-estate-plan-presets';
import type { ProductCode } from './commercialProducts';
import {
  isEnterpriseSeatBand,
  pricedSeatBandCodes,
  type PricedSeatBandCode,
  type SeatBandCode,
} from './seatBands';
import type { AICapabilityCode } from './aiCapabilities';

export type PlanCode = RealEstatePlanCode;
export type PaidPlanCode = Exclude<PlanCode, 'free'>;
export type BillingCadence = 'monthly' | 'annual';

interface CommercialPlanBase {
  nameKey: string;
  descriptionKey: string;
  includesModules: ProductCode[];
  comingSoonModules: ProductCode[];
  featuredAiCapabilities: AICapabilityCode[];
}

export type CommercialPlan = CommercialPlanBase &
  (
    | {
        code: 'free';
        ctaType: 'self_service';
        popular?: false;
      }
    | {
        code: PaidPlanCode;
        ctaType: 'demo' | 'contact';
        popular?: boolean;
      }
  );

export interface PlanSeatBandPrice {
  monthlyPerLicenseUsd: number;
  annualPerLicenseUsd: number;
}

export interface FreePlanCommercialTerms {
  monthlyPriceUsd: number;
  individualActiveUsers: number;
  businessActiveUsers: number;
}

export type PlanPriceMatrix = Record<
  PaidPlanCode,
  Record<PricedSeatBandCode, PlanSeatBandPrice>
>;

export interface RealEstatePricingSchema {
  industry: 'real_estate';
  currency: 'USD';
  monthlyBillingMonths: number;
  annualBillingMonths: number;
  annualFreeMonths: number;
  freeTerms: FreePlanCommercialTerms;
  prices: PlanPriceMatrix;
}

const MONTHS_IN_YEAR = 12;
const ANNUAL_BILLING_MONTHS = 10;
const FREE_MONTHLY_PRICE_USD = 0;
const BASIC_SMALL_MONTHLY_PRICE_USD = 40;
const BASIC_GROWTH_MONTHLY_PRICE_USD = 30;
const PLUS_SMALL_MONTHLY_PRICE_USD = 45;
const PLUS_GROWTH_MONTHLY_PRICE_USD = 35;
const PRO_SMALL_MONTHLY_PRICE_USD = 55;
const PRO_GROWTH_MONTHLY_PRICE_USD = 45;

function toAnnualPerLicenseUsd(monthlyPerLicenseUsd: number): number {
  return monthlyPerLicenseUsd * ANNUAL_BILLING_MONTHS;
}

export const realEstatePricingSchema: RealEstatePricingSchema = {
  industry: 'real_estate',
  currency: 'USD',
  monthlyBillingMonths: MONTHS_IN_YEAR,
  annualBillingMonths: ANNUAL_BILLING_MONTHS,
  annualFreeMonths: MONTHS_IN_YEAR - ANNUAL_BILLING_MONTHS,
  freeTerms: {
    monthlyPriceUsd: FREE_MONTHLY_PRICE_USD,
    individualActiveUsers: 1,
    businessActiveUsers: 2,
  },
  prices: {
    basic: {
      '1-3': {
        monthlyPerLicenseUsd: BASIC_SMALL_MONTHLY_PRICE_USD,
        annualPerLicenseUsd: toAnnualPerLicenseUsd(
          BASIC_SMALL_MONTHLY_PRICE_USD
        ),
      },
      '4-49': {
        monthlyPerLicenseUsd: BASIC_GROWTH_MONTHLY_PRICE_USD,
        annualPerLicenseUsd: toAnnualPerLicenseUsd(
          BASIC_GROWTH_MONTHLY_PRICE_USD
        ),
      },
    },
    plus: {
      '1-3': {
        monthlyPerLicenseUsd: PLUS_SMALL_MONTHLY_PRICE_USD,
        annualPerLicenseUsd: toAnnualPerLicenseUsd(
          PLUS_SMALL_MONTHLY_PRICE_USD
        ),
      },
      '4-49': {
        monthlyPerLicenseUsd: PLUS_GROWTH_MONTHLY_PRICE_USD,
        annualPerLicenseUsd: toAnnualPerLicenseUsd(
          PLUS_GROWTH_MONTHLY_PRICE_USD
        ),
      },
    },
    pro: {
      '1-3': {
        monthlyPerLicenseUsd: PRO_SMALL_MONTHLY_PRICE_USD,
        annualPerLicenseUsd: toAnnualPerLicenseUsd(PRO_SMALL_MONTHLY_PRICE_USD),
      },
      '4-49': {
        monthlyPerLicenseUsd: PRO_GROWTH_MONTHLY_PRICE_USD,
        annualPerLicenseUsd: toAnnualPerLicenseUsd(
          PRO_GROWTH_MONTHLY_PRICE_USD
        ),
      },
    },
  },
};

export const planPriceMatrixUsd: PlanPriceMatrix =
  realEstatePricingSchema.prices;

export const commercialPlans: CommercialPlan[] = [
  {
    code: 'free',
    nameKey: 'pricing:plans.free.name',
    descriptionKey: 'pricing:plans.free.description',
    includesModules: [...REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES.free],
    comingSoonModules: [],
    featuredAiCapabilities: ['marketplace_guided_search'],
    ctaType: 'self_service',
  },
  {
    code: 'basic',
    nameKey: 'pricing:plans.basic.name',
    descriptionKey: 'pricing:plans.basic.description',
    includesModules: [...REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES.basic],
    comingSoonModules: [],
    featuredAiCapabilities: ['marketplace_guided_search', 'smart_filter'],
    ctaType: 'demo',
  },
  {
    code: 'plus',
    nameKey: 'pricing:plans.plus.name',
    descriptionKey: 'pricing:plans.plus.description',
    includesModules: [...REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES.plus],
    comingSoonModules: [],
    featuredAiCapabilities: [
      'collections_recommendation',
      'voucher_ocr',
      'marketplace_guided_search',
      'smart_filter',
    ],
    ctaType: 'demo',
    popular: true,
  },
  {
    code: 'pro',
    nameKey: 'pricing:plans.pro.name',
    descriptionKey: 'pricing:plans.pro.description',
    includesModules: [...REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES.pro],
    comingSoonModules: ['investments', 'dataroom', 'project_reporting'],
    featuredAiCapabilities: [
      'collections_recommendation',
      'voucher_ocr',
      'marketplace_guided_search',
      'smart_filter',
      'predictive_scoring',
      'next_best_action',
    ],
    ctaType: 'demo',
  },
];

export const paidCommercialPlans = commercialPlans.filter(
  (plan): plan is CommercialPlan & { code: PaidPlanCode } =>
    isPaidPlanCode(plan.code)
);

export function isPaidPlanCode(planCode: PlanCode): planCode is PaidPlanCode {
  return planCode !== 'free';
}

function assertCommercialPlanRecommendationInvariant(
  plans: readonly CommercialPlan[]
): void {
  const recommendedPlans = plans.filter((plan) => plan.popular === true);
  if (recommendedPlans.length !== 1) {
    throw new Error(
      `Commercial plan configuration requires exactly one recommended plan; found ${recommendedPlans.length}`
    );
  }

  const recommendedPlan = recommendedPlans[0];
  if (recommendedPlan.ctaType === 'self_service') {
    throw new Error(
      `Self-service plan ${recommendedPlan.code} cannot be the recommended paid plan`
    );
  }
}

assertCommercialPlanRecommendationInvariant(commercialPlans);

export function getPlanPriceUsd(
  planCode: PaidPlanCode,
  seatBandCode: SeatBandCode,
  billingCadence: BillingCadence
): number | null {
  if (isEnterpriseSeatBand(seatBandCode)) {
    return null;
  }

  const bandPricing = planPriceMatrixUsd[planCode][seatBandCode];
  return billingCadence === 'annual'
    ? bandPricing.annualPerLicenseUsd
    : bandPricing.monthlyPerLicenseUsd;
}

export function getPlanStartingPriceUsd(
  planCode: PaidPlanCode,
  billingCadence: BillingCadence
): number {
  return Math.min(
    ...pricedSeatBandCodes.map((seatBandCode) =>
      getPlanPriceUsd(planCode, seatBandCode, billingCadence)!
    )
  );
}
